<script setup>
import { computed } from 'vue'

const props = defineProps({
  variant: { type: String, default: 'secondary' }, // primary | secondary | ghost | danger | success | link
  size: { type: String, default: 'md' }, // xs | sm | md
  icon: { type: String, default: '' },
  iconRight: { type: String, default: '' },
  loading: Boolean,
  disabled: Boolean,
  type: { type: String, default: 'button' },
  title: { type: String, default: '' },
  block: Boolean,
  // A link that looks like a button (a route): "New …" leading to a page.
  to: { type: [String, Object], default: null },
})

const classes = computed(() => {
  const base = 'inline-flex shrink-0 items-center justify-center gap-1.5 font-medium rounded-lg transition-colors select-none whitespace-nowrap focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 disabled:opacity-50 disabled:cursor-not-allowed'
  const sizes = { xs: 'text-xs px-2 py-1', sm: 'text-btn px-2.5 py-1.5', md: 'text-sm px-3.5 py-2' }
  const variants = {
    primary: 'bg-brand-600 text-white hover:bg-brand-700 shadow-sm',
    secondary: 'surface border border-default text-[var(--text)] hover:bg-[var(--surface-3)]',
    ghost: 'text-muted hover:bg-[var(--surface-3)] hover:text-[var(--text)]',
    danger: 'bg-red-600 text-white hover:bg-red-700 shadow-sm',
    success: 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm',
    link: 'link px-0 py-0',
  }
  return [base, sizes[props.size] || sizes.md, variants[props.variant] || variants.secondary, props.block ? 'w-full' : '']
})
</script>

<template>
  <router-link v-if="to" :to="to" :class="classes" :title="title || undefined">
    <i v-if="icon" :class="['fa-solid', icon]" aria-hidden="true"></i>
    <slot />
    <i v-if="iconRight" :class="['fa-solid', iconRight, 'text-xs opacity-70']" aria-hidden="true"></i>
  </router-link>
  <button v-else :type="type" :class="classes" :disabled="disabled || loading" :title="title || undefined" :aria-busy="loading || undefined">
    <i v-if="loading" class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>
    <i v-else-if="icon" :class="['fa-solid', icon]" aria-hidden="true"></i>
    <slot />
    <i v-if="iconRight" :class="['fa-solid', iconRight, 'text-xs opacity-70']" aria-hidden="true"></i>
  </button>
</template>
